/**
 * The safety net that keeps the header you clicked where it was when its group opens or
 * closes.
 *
 * The first line of defence is the rule in lib/sidebar/open-groups.ts: opening a group
 * closes only the open groups BELOW it, so nothing above the clicked header changes height
 * and it cannot move. Staging QA (16 Oct, 1366x768) showed what the old strict accordion
 * did: opening Phishing while Recipients (above) was open took its header from y=630 to 309
 * within 80 ms, because Recipients collapsed in the same frame, and to 114 by 700 ms,
 * because a "reveal what you opened" scroll then lifted it further. A header click now never
 * scrolls the menu at all: a group that runs past the bottom is reached by the person's own
 * scroll (a ROUTE change that leaves the active row out of view is the one automatic scroll
 * left, `revealRowIfHidden` below).
 *
 * What is left for this module is the case where the layout still shifts under the header:
 * a group closing BELOW it (or the header's own group collapsing at the end of the list)
 * shortens the content, and the browser clamps the scroll offset to what is left
 * (400 -> 112 on staging; the header went 78 -> 366). The cure is the one a browser's own
 * scroll anchoring attempts (it did nothing here, measured in Chromium) and a
 * conditionally-rendered tree defeats: measure the header BEFORE the state changes
 * (`captureHeaderHold`), and once React has rewritten the DOM but before the browser paints
 * (a layout effect), move the offset by exactly how far the header drifted
 * (`applyHeaderHold`).
 *
 * A scroll offset can only go so far, and both ends are handled rather than hidden:
 *
 *   - past the BOTTOM: after a collapse the list can be too short for the offset
 *     the header needs. The panel gets blank room at the end of its content (a
 *     `::after` flex item, see globals.css) for the difference, and gives it back
 *     once that blank area scrolls out of view, instead of letting the browser
 *     clamp. Padding would not do: the panel has a fixed height, so padding just
 *     eats the room inside it.
 *   - past the TOP: if the panel is already at (or near) its top, no offset can
 *     hold a header whose upstairs neighbour just closed, because it would have to
 *     be negative. The header cannot stay; what we can do is keep it from
 *     teleporting. The panel is padded at the top by the missing amount, which
 *     puts the header exactly where the pointer left it, and the padding then
 *     eases away, so the header slides up to its new place under the person's
 *     eyes. With `prefers-reduced-motion`, or no Web Animations, it settles
 *     straight at the nearest position, which is on screen.
 *
 * Nothing here calls `scrollIntoView`: that walks every scrollable ancestor,
 * the page included, and it is what a "bring the active row into view" effect
 * would use to shove the panel somewhere the person did not ask to go.
 */

/** The panel the menu scrolls in. `SidebarContent` marks itself with this slot. */
const SCROLLER_SELECTOR = '[data-slot="sidebar-content"]'

/** Differences smaller than this are sub-pixel layout noise, not a move. */
const EPSILON = 0.5

/** How long the header takes to slide to a position it cannot hold. */
export const GLIDE_MS = 220

/** The CSS custom property and attribute behind the blank room (see globals.css). */
const ROOM_VAR = '--hf-sidebar-room'
const ROOM_ATTR = 'data-hold-room'

export interface HeaderHold {
  header: HTMLElement
  scroller: HTMLElement
  /** `header.getBoundingClientRect().top` before the toggle. */
  top: number
}

interface AddedRoom {
  /** Pixels of blank room this module has added at the end of the panel's content. */
  px: number
  onScroll: () => void
}

const room = new WeakMap<HTMLElement, AddedRoom>()
const glides = new WeakMap<HTMLElement, Animation>()

function isScrollable(el: HTMLElement): boolean {
  const overflowY = window.getComputedStyle(el).overflowY
  return overflowY === 'auto' || overflowY === 'scroll'
}

/** The sidebar panel the header lives in, or the nearest scrolling ancestor. */
export function findMenuScroller(header: HTMLElement): HTMLElement | null {
  const panel = header.closest<HTMLElement>(SCROLLER_SELECTOR)
  if (panel) return panel
  for (let el = header.parentElement; el; el = el.parentElement) {
    if (isScrollable(el)) return el
  }
  return null
}

/** Call from the click handler, BEFORE the open/closed state changes. */
export function captureHeaderHold(header: HTMLElement): HeaderHold | null {
  const scroller = findMenuScroller(header)
  if (!scroller) return null
  return { header, scroller, top: header.getBoundingClientRect().top }
}

export function prefersReducedMotion(): boolean {
  try {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  } catch {
    return false
  }
}

/** Gives back the blank room added to hold a header. Safe to call anytime. */
export function releaseHoldRoom(scroller: HTMLElement) {
  const current = room.get(scroller)
  if (!current) return
  scroller.removeEventListener('scroll', current.onScroll)
  scroller.removeAttribute(ROOM_ATTR)
  scroller.style.removeProperty(ROOM_VAR)
  room.delete(scroller)
}

/**
 * How much room, in px, must follow the content so the panel can scroll to
 * `offset`. A panel taller than its content reports its OWN height as
 * `scrollHeight`, so that number says nothing about how short the content is:
 * measure where the last row ends instead.
 */
function roomNeededFor(scroller: HTMLElement, offset: number): number {
  const needed = offset + scroller.clientHeight
  const last = scroller.lastElementChild
  const style = window.getComputedStyle(scroller)
  if (!last) return Math.ceil(needed - (scroller.scrollHeight - (room.get(scroller)?.px ?? 0)))
  const contentEnd =
    last.getBoundingClientRect().bottom - scroller.getBoundingClientRect().top - scroller.clientTop + scroller.scrollTop
  const padding = Number.parseFloat(style.paddingBottom) || 0
  // The room is a flex item, so the panel's row gap sits between it and the content.
  const gap = Number.parseFloat(style.rowGap) || 0
  return Math.ceil(needed - (contentEnd + padding + gap))
}

function setHoldRoom(scroller: HTMLElement, px: number) {
  if (px <= 0) {
    releaseHoldRoom(scroller)
    return
  }
  scroller.setAttribute(ROOM_ATTR, '')
  scroller.style.setProperty(ROOM_VAR, `${px}px`)
  const current = room.get(scroller)
  if (current) {
    current.px = px
    return
  }
  const onScroll = () => {
    const added = room.get(scroller)?.px ?? 0
    // The visible area ends above the blank we added: giving it back moves nothing.
    if (scroller.scrollTop + scroller.clientHeight <= scroller.scrollHeight - added) releaseHoldRoom(scroller)
  }
  scroller.addEventListener('scroll', onScroll, { passive: true })
  room.set(scroller, { px, onScroll })
}

/** Stops a header slide still in flight, so the next measurement sees real positions. */
export function cancelHeaderGlide(scroller: HTMLElement) {
  const running = glides.get(scroller)
  if (!running) return
  glides.delete(scroller)
  running.cancel()
}

/** Eases `px` of top padding on the panel away. False when it cannot be animated. */
function glideFrom(scroller: HTMLElement, px: number, reducedMotion: boolean): boolean {
  if (reducedMotion || px <= EPSILON || typeof scroller.animate !== 'function') return false
  const own = Number.parseFloat(window.getComputedStyle(scroller).paddingTop) || 0
  const animation = scroller.animate(
    [{ paddingTop: `${own + px}px` }, { paddingTop: `${own}px` }],
    { duration: GLIDE_MS, easing: 'ease-out' },
  )
  glides.set(scroller, animation)
  const done = () => {
    if (glides.get(scroller) === animation) glides.delete(scroller)
  }
  animation.addEventListener('finish', done)
  animation.addEventListener('cancel', done)
  return true
}

/**
 * Call from a layout effect once the toggle has been committed. Puts the header
 * back where `captureHeaderHold` saw it, as far as the panel's scroll range
 * allows. It never scrolls to show what the click opened.
 */
export function applyHeaderHold(hold: HeaderHold, opts: { reducedMotion?: boolean } = {}) {
  const { header, scroller } = hold
  if (!header.isConnected || !scroller.isConnected) return
  const reducedMotion = opts.reducedMotion ?? prefersReducedMotion()

  // A slide from the previous click is still easing its padding away: stop it
  // first, so the measurement below is of where things really are.
  cancelHeaderGlide(scroller)

  // Reading the rect forces layout, so a browser's own anchoring has already
  // had its say: what is measured is where the header really is.
  const drift = header.getBoundingClientRect().top - hold.top
  if (Math.abs(drift) > EPSILON) {
    const wanted = scroller.scrollTop + drift
    if (wanted > 0) {
      // A browser clamps the offset to the content it has. Lengthen the content
      // by exactly what is missing so the offset the header needs exists.
      setHoldRoom(scroller, roomNeededFor(scroller, wanted))
      scroller.scrollTop = wanted
    } else {
      // At the top edge: no offset can hold the header. Nothing is needed at the
      // bottom, and the header slides to the nearest position instead of jumping.
      releaseHoldRoom(scroller)
      scroller.scrollTop = 0
      const missing = hold.top - header.getBoundingClientRect().top
      glideFrom(scroller, missing, reducedMotion)
    }
  }

  // Room added by an earlier click stays only as long as the offset needs it:
  // opening the group again makes the list long enough to scroll there unaided.
  if (room.has(scroller)) setHoldRoom(scroller, roomNeededFor(scroller, scroller.scrollTop))
}

/**
 * Scrolls the panel by the least that shows `row`, and not at all when it is
 * already in view: `scrollIntoView({ block: 'nearest' })` for the panel alone.
 * A row taller than the panel is aligned at its top edge.
 *
 * Used when a ROUTE change (never a click on a header) moves the open group: the
 * "you are here" row may then sit below the fold of a panel that was scrolled
 * for the previous module.
 */
export function revealRowIfHidden(row: HTMLElement, scroller: HTMLElement) {
  const panel = scroller.getBoundingClientRect()
  const box = row.getBoundingClientRect()
  let by = 0
  if (box.top < panel.top) {
    by = box.top - panel.top
  } else if (box.bottom > panel.bottom) {
    by = Math.min(box.bottom - panel.bottom, box.top - panel.top)
  }
  if (Math.abs(by) > EPSILON) scroller.scrollTop += by
}
